/**
 * Handschutzplan-Modul · Baustein 5 (Aug 2026) — Owner-Übersichtsseite.
 *
 * Drill-down-Struktur (Betreiberfreigabe 3a): Liste aller AKTIVEN
 * Einsatzbereiche des Mandanten mit Plan-Status + „Jetzt generieren".
 * Klick auf einen Bereich öffnet die Detailseite. Vorbild:
 * AushangPflicht.jsx (403 → „nicht freigeschaltet", Server-Gate).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  formatDateDe,
  generateFehlerMeldung,
  istPruefungBaldFaellig,
  istPruefungFaellig,
} from "@/lib/handschutzplan";

function StatusText({ plan }) {
  if (!plan?.generated_at) {
    return <span className="text-slate-500">Noch nicht generiert</span>;
  }
  return (
    <span className="text-slate-600">
      Generiert am {formatDateDe(plan.generated_at)}
    </span>
  );
}

// Baustein 8: Anzeige-Labels für die Intervall-Stufen (FSK-Konsistenz).
const INTERVALL_LABELS = {
  3: "3 Monate (vierteljährlich)",
  6: "6 Monate (halbjährlich)",
  9: "9 Monate",
  12: "12 Monate (jährlich)",
};

export default function Handschutzplan() {
  const [bereiche, setBereiche] = useState(null);
  const [plaene, setPlaene] = useState({});
  const [forbidden, setForbidden] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [busyId, setBusyId] = useState(null);
  // Baustein 8: Mandanten-Prüfintervall (fehlertolerant geladen —
  // ohne Daten erscheint die Einstellungs-Box schlicht nicht).
  const [einstellungen, setEinstellungen] = useState(null);
  // Baustein 9-Erweiterung (Feb 2026, LUMPE-Datenqualitäts-Hinweis):
  // Anzahl Registry-Einträge ohne Einsatzbereichs-Zuordnung. Server-
  // gegated — bei 403 (Modul aus) still auf 0 fallen, damit der Hinweis
  // nicht auftaucht.
  const [waisen, setWaisen] = useState(0);

  const load = useCallback(() => {
    Promise.all([api.listEinsatzbereiche(false), api.listHandschutzplaene()])
      .then(([ebs, plans]) => {
        setBereiche(Array.isArray(ebs) ? ebs : []);
        setPlaene(
          Object.fromEntries(
            (Array.isArray(plans) ? plans : []).map((p) => [
              p.einsatzbereich_id,
              p,
            ])
          )
        );
      })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else setErrorMsg("Handschutzpläne konnten nicht geladen werden.");
      });
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    api
      .getHandschutzplanEinstellungen()
      .then(setEinstellungen)
      .catch(() => {});
  }, []);

  // Baustein 9-Erweiterung: Waisen-Zähler laden. Silent fallback — der
  // Hinweis ist rein informativ und darf keinen Fehler-Toast erzeugen.
  useEffect(() => {
    api
      .getHandschutzplanRegistryLuecken()
      .then((data) => setWaisen(Number(data?.waisen) || 0))
      .catch(() => setWaisen(0));
  }, []);

  const handleIntervallChange = async (e) => {
    const wert = Number(e.target.value);
    const vorher = einstellungen;
    setEinstellungen((prev) => ({ ...prev, pruefintervall_monate: wert }));
    try {
      await api.updateHandschutzplanEinstellungen({
        pruefintervall_monate: wert,
      });
      toast.success(
        "Prüfintervall gespeichert — gilt für künftig generierte Pläne."
      );
    } catch {
      setEinstellungen(vorher);
      toast.error("Prüfintervall konnte nicht gespeichert werden.");
    }
  };

  const handleGenerate = async (ebId) => {
    setBusyId(ebId);
    try {
      const doc = await api.generateHandschutzplan(ebId);
      setPlaene((prev) => ({ ...prev, [ebId]: doc }));
      if (doc?.worm_status === "error") {
        toast.warning(
          "Plan erstellt — der Nachweis-Beleg konnte jedoch nicht abgelegt werden."
        );
      } else {
        toast.success("Handschutzplan wurde erstellt.");
      }
    } catch (err) {
      toast.error(generateFehlerMeldung(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="handschutzplan-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
          Gefahrstoff-Modul
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
          Hautschutzplan
        </h1>
        <p className="text-slate-600 mt-2">
          Hautschutz- und Handschutzplan je Einsatzbereich — erstellt aus Ihren
          Gefahrstoff-Daten und den Tätigkeiten Ihres Betriebs.
        </p>
      </div>

      {!forbidden && !errorMsg && waisen > 0 && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-lg p-4 text-sm"
          data-testid="hsp-registry-luecken-hinweis"
          role="status"
        >
          <div className="font-heading font-semibold text-amber-900">
            {waisen === 1
              ? "1 Gefahrstoff ohne Bereichszuordnung"
              : `${waisen} Gefahrstoffe ohne Bereichszuordnung`}
          </div>
          <div className="text-amber-800 mt-1">
            Diese Gefahrstoffe fließen aktuell in keinen Handschutzplan ein.
            Sie können ihre Einsatzbereichs-Zuordnung im Gefahrstoffverzeichnis
            pflegen — die Zuordnung wandert dann automatisch in die
            Plan-Erstellung. Die zugehörige Owner-Ansicht wird in einer
            künftigen Ausbaustufe direkt hier verlinkt.
          </div>
        </div>
      )}

      {!forbidden && !errorMsg && einstellungen && (
        <div
          className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="hsp-intervall-box"
        >
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-slate-900 text-sm">
              Prüfintervall
            </div>
            <div
              className="text-xs text-slate-500 mt-0.5"
              data-testid="hsp-intervall-hint"
            >
              Gilt für künftig generierte Pläne — bestehende Fälligkeiten
              bleiben bis zur nächsten Neugenerierung unverändert.
            </div>
          </div>
          <select
            value={einstellungen.pruefintervall_monate}
            onChange={handleIntervallChange}
            data-testid="hsp-intervall-select"
            className="shrink-0 rounded-md border border-slate-300 bg-white text-sm text-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            {(einstellungen.erlaubte_intervalle || [3, 6, 9, 12]).map((m) => (
              <option key={m} value={m}>
                {INTERVALL_LABELS[m] || `${m} Monate`}
              </option>
            ))}
          </select>
        </div>
      )}

      {forbidden ? (
        <div
          className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-4 text-sm"
          data-testid="handschutzplan-forbidden"
        >
          Das Gefahrstoff-Modul ist für Ihren Betrieb nicht freigeschaltet.
        </div>
      ) : errorMsg ? (
        <div
          className="border border-red-200 bg-red-50 text-red-900 rounded-md p-4 text-sm"
          data-testid="handschutzplan-error"
        >
          {errorMsg}
        </div>
      ) : bereiche === null ? (
        <div className="text-slate-500 text-sm" data-testid="handschutzplan-loading">
          Lade…
        </div>
      ) : bereiche.length === 0 ? (
        <div
          className="border border-slate-200 bg-white rounded-md p-6 text-sm text-slate-600"
          data-testid="handschutzplan-empty"
        >
          Es sind noch keine Einsatzbereiche angelegt.
        </div>
      ) : (
        <div className="space-y-3">
          {bereiche.map((eb) => {
            const plan = plaene[eb.id];
            const faellig = istPruefungFaellig(plan);
            const baldFaellig = istPruefungBaldFaellig(plan);
            const busy = busyId === eb.id;
            return (
              <div
                key={eb.id}
                className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`hsp-row-${eb.id}`}
              >
                <Link
                  to={`/unternehmer/handschutzplan/${eb.id}`}
                  className="flex-1 min-w-0 group"
                  data-testid={`hsp-row-link-${eb.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-slate-900 group-hover:underline truncate">
                      {eb.name}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    {faellig && (
                      <span
                        className="inline-flex items-center rounded-full bg-red-100 text-red-800 text-[11px] font-semibold px-2 py-0.5"
                        data-testid={`hsp-due-badge-${eb.id}`}
                      >
                        Zur Prüfung fällig
                      </span>
                    )}
                    {baldFaellig && (
                      <span
                        className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5"
                        data-testid={`hsp-due-soon-badge-${eb.id}`}
                      >
                        Prüfung bald fällig
                      </span>
                    )}
                  </div>
                  <div
                    className="text-sm mt-1"
                    data-testid={`hsp-status-${eb.id}`}
                  >
                    <StatusText plan={plan} />
                  </div>
                </Link>
                <div className="shrink-0 flex flex-col items-start sm:items-end gap-1">
                  <button
                    onClick={() => handleGenerate(eb.id)}
                    disabled={busyId !== null}
                    data-testid={`hsp-generate-btn-${eb.id}`}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    {plan?.generated_at ? "Neu generieren" : "Jetzt generieren"}
                  </button>
                  {busy && (
                    <span
                      className="text-[11px] text-slate-500"
                      data-testid={`hsp-generate-hint-${eb.id}`}
                    >
                      Erstellung läuft — kann bis zu zwei Minuten dauern…
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
